import { isValidNumber } from '@utils/utilities'
import { Flex } from 'antd'
import React from 'react'

import type { AvatarSectionProps } from '../../types/avatar'
import {
  isAvatarUnlocked,
  isSpecialAvatarUnlocked
} from '../../utils/avatarUtils'

const AvatarSection: React.FC<AvatarSectionProps> = React.memo(
  ({
    avatars,
    lockedAvatars,
    title,
    stickerRequirement,
    currentStickers = 0,
    currentLevel,
    onAvatarClick,
    isSpecialSection = false,
    isSpecialStickerSection = false
  }) => {
    const kidClasses =
      stickerRequirement === 0
        ? 'avatar-leftbox__kidrowicon additional-kid-css'
        : ''
    const kidClassessection =
      stickerRequirement === 0 ? 'avatar-leftbox__kidicons' : ''

    return (
      <>
        {title && <p className="fw-bold">{title}</p>}
        <Flex
          gap={10}
          wrap
          className={`avatar-leftbox__icons ${kidClassessection}`}
        >
          {avatars.map((avatar, index) => {
            let isUnlocked: boolean

            if (isSpecialStickerSection && isValidNumber(currentLevel)) {
              // Special sticker avatars need both high level and high sticker count
              isUnlocked = isSpecialAvatarUnlocked(
                index,
                currentLevel,
                currentStickers
              )
            } else if (isSpecialSection && isValidNumber(currentLevel)) {
              // Regular skill level avatars unlock by level only
              isUnlocked = isAvatarUnlocked(0, 0, index, currentLevel)
            } else {
              // Regular sticker-based avatars
              isUnlocked = stickerRequirement
                ? isAvatarUnlocked(stickerRequirement, currentStickers)
                : true
            }

            // Handle special sections (skill level and special sticker avatars)
            if (isSpecialSection || isSpecialStickerSection) {
              if (!isUnlocked && lockedAvatars) {
                const lockedAvatar = lockedAvatars[index]
                if (lockedAvatar) {
                  return (
                    <button
                      key={`locked-${avatar.id}`}
                      type="button"
                      title={`${avatar.name} (locked)`}
                      aria-label={`${avatar.name} avatar (locked)`}
                      className="avatar-leftbox__btn"
                      style={{ pointerEvents: 'none' }}
                    >
                      <lockedAvatar.component />
                    </button>
                  )
                }
                return null // Don't render if no locked version available
              }

              if (isUnlocked) {
                return (
                  <button
                    key={avatar.id}
                    type="button"
                    onClick={() => onAvatarClick(avatar)}
                    title={avatar.name}
                    aria-label={`Select ${avatar.name} avatar`}
                    className="avatar-leftbox__btn"
                  >
                    <avatar.component />
                  </button>
                )
              }

              return null
            }

            // Handle regular sticker-based sections (always render, but with different styles)
            return (
              <button
                key={avatar.id}
                type="button"
                onClick={isUnlocked ? () => onAvatarClick(avatar) : undefined}
                title={isUnlocked ? avatar.name : `${avatar.name} (locked)`}
                aria-label={
                  isUnlocked
                    ? `Select ${avatar.name} avatar`
                    : `${avatar.name} avatar (locked)`
                }
                className={
                  isUnlocked
                    ? `avatar-leftbox__btn avatar-leftbox__iconbg ${kidClasses}`
                    : 'avatar-leftbox__iconlock'
                }
                style={!isUnlocked ? { pointerEvents: 'none' } : undefined}
              >
                <avatar.component />
              </button>
            )
          })}
        </Flex>
      </>
    )
  }
)

AvatarSection.displayName = 'AvatarSection'
export default AvatarSection
